'use client'

import { useEffect, useMemo } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { ParsedUrlQuery } from 'querystring'

import {
  CustomerTypes,
  extractCustomerFromClientSession
} from 'modules/customer'
import {
  getNextMultipleProductsByIdsPage,
  ProductTypes,
  useInfiniteScroll
} from 'modules/products'
import { distributionCenterId } from 'shared/config/distributionCenter'
import { PRODUCTS_LISTING_LIMIT } from 'shared/utils/common'
import { PATHS } from 'shared/utils/paths'

import { useQuerySearchProducts } from '../queries/useQuerySearchProducts'
import useSearchResultsStore from '../store/searchStore'
import { SearchParamsKeys } from '../types'

type UseSearchPageParams = {
  token: string
}

export const useSearchPage = ({ token }: UseSearchPageParams) => {
  const router = useRouter()
  const session = useSession()
  const customer: CustomerTypes | undefined =
    extractCustomerFromClientSession(session)
  const searchParams = useSearchParams()
  const {
    prefetchedProductIds,
    prefetchedQuery,
    prefetchedQueryID,
    clearPrefetchedResultsData
  } = useSearchResultsStore()

  const query = searchParams.get(SearchParamsKeys.searchQuery)?.trim() ?? ''
  const queryParams = useMemo(
    () => Object.fromEntries(searchParams.entries()) as ParsedUrlQuery,
    [searchParams]
  )

  // Redirect on empty query
  useEffect(() => {
    if (!query) {
      router.replace(PATHS.products)
    }
  }, [query, router])

  const {
    data: initialProducts,
    productIds,
    queryID,
    hasResolvedResults,
    hasFailedToFetchProductsDetails
  } = useQuerySearchProducts({
    customer,
    prefetchedProductIds,
    prefetchedQuery,
    prefetchedQueryID,
    clearPrefetchedResultsData,
    query,
    token
  })

  const isLoading =
    query.length > 0 && !hasResolvedResults && !hasFailedToFetchProductsDetails

  const { productsData, isFetching, ref } = useInfiniteScroll<ProductTypes>({
    products: initialProducts?.data?.data ?? [],
    query: queryParams,
    key: 'algoliaSearchPageInfiniteScroll',
    token,
    distributionCenterId,
    getNextPageCallback: getNextMultipleProductsByIdsPage,
    productIds,
    limit: PRODUCTS_LISTING_LIMIT
  })

  return {
    query,
    queryID,
    customerId: customer?.id,
    isLoading,
    productsData,
    productsMeta: initialProducts?.data?.meta ?? {
      total: 0,
      lastPage: 0,
      currentPage: 0
    },
    isFetching,
    ref,
    hasFailedToFetchProductsDetails
  }
}
